import { useEffect, useMemo } from 'react';
import {
  ArrayParam,
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { useTaskManagerContext } from './TaskManagerContext';
import { TaskStatus, Team } from './types';
import { getJobTypesForTeam } from './teamJobLookup';

const ALL_ASSIGNED = undefined;
const UNASSIGNED = null;

const getAssignmentDefault = (
  isBpaView: boolean,
  requireTeam: boolean,
  loggedInPersonId: string,
) => {
  if (isBpaView) {
    return Number(loggedInPersonId);
  } else if (requireTeam) {
    return UNASSIGNED;
  }
  // Client Task Manager
  return ALL_ASSIGNED;
};

export const useQueryParamsWithDefaults = (isBpaView: boolean, requireTeam: boolean) => {
  const { loggedInPerson } = useTaskManagerContext();
  const defaultFilterBy = useMemo(
    () => ({
      assignedPersonId: getAssignmentDefault(isBpaView, requireTeam, loggedInPerson.id),
      clientIds: [],
      bookkeeperIds: [],
      taskIds: [],
      team: requireTeam ? Team.DOCUMENT_SORTING : undefined,
      jobTypes: requireTeam ? getJobTypesForTeam(Team.DOCUMENT_SORTING) : [],
      status: TaskStatus.PENDING_WORK,
    }),
    [isBpaView, loggedInPerson, requireTeam],
  );

  const [query, setQuery] = useQueryParams({
    assignedPersonId: isBpaView
      ? withDefault(NumberParam, defaultFilterBy.assignedPersonId)
      : NumberParam,
    status: withDefault(StringParam, TaskStatus.PENDING_WORK),
    team: requireTeam ? withDefault(StringParam, defaultFilterBy.team) : StringParam,
    taskTypes: ArrayParam,
    jobTypes:
      requireTeam && defaultFilterBy.team
        ? withDefault(ArrayParam, getJobTypesForTeam(defaultFilterBy.team))
        : ArrayParam,
    inboxTypes: ArrayParam,
    isEscalated: BooleanParam,
    bookkeeperIds: withDefault(ArrayParam, defaultFilterBy.bookkeeperIds),
    clientIds: withDefault(ArrayParam, defaultFilterBy.clientIds),
    taskIds: withDefault(ArrayParam, defaultFilterBy.taskIds),
  });

  useEffect(() => {
    if (location.search !== '') return;
    setQuery(defaultFilterBy);
  }, [defaultFilterBy, setQuery]);

  return { query, setQuery };
};
